import { useCallback, useMemo, useState } from 'react';
import type { SubmissionShort } from '@queries/index';
import { sortByDate } from '@root/helpers';
import { rankings, rankItem } from '@tanstack/match-sorter-utils';

interface UseCloneEndorsementSelectionProps {
  contracts: SubmissionShort[];
}

export function useCloneEndorsementSelection({ contracts }: UseCloneEndorsementSelectionProps) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedContracts, setSelectedContracts] = useState<SubmissionShort[]>([]);

  const filteredContracts = useMemo(() => {
    const notSelectedContracts = sortByDate(
      contracts.filter(({ id }) => !selectedContracts.some((contract) => contract.id === id)),
      'desc',
      'created_at',
    );

    if (!searchValue.trim()) return notSelectedContracts;

    return notSelectedContracts
      .map((contract) => {
        const refRank = rankItem(contract.ref, searchValue, { threshold: rankings.CONTAINS });
        const nameRank = rankItem(contract.name, searchValue, { threshold: rankings.CONTAINS });

        const bestRank = refRank.rank > nameRank.rank ? refRank : nameRank;
        return { contract, rank: bestRank };
      })
      .filter(({ rank }) => rank.passed)
      .sort((a, b) => b.rank.rank - a.rank.rank)
      .map(({ contract }) => contract);
  }, [contracts, searchValue, selectedContracts]);

  const addContract = useCallback((contract: SubmissionShort) => {
    setSelectedContracts((previousContracts) => [...previousContracts, contract]);
  }, []);

  const removeContract = useCallback((contractId: string) => {
    setSelectedContracts((previousContracts) => previousContracts.filter((contract) => contract.id !== contractId));
  }, []);

  return {
    addContract,
    filteredContracts,
    removeContract,
    searchValue,
    selectedContracts,
    setSearchValue,
    selectedCount: selectedContracts.length,
  };
}
